import React from 'react';
import { RotateCcw } from 'lucide-react';
import type { ResearchRunEnd } from '../services/researchRunStore';

/** Titel und Hinweis des Endzustands; der Lauf meldet bei einem Fehler nur seinen Code. */
export function researchRunStatusText(runEnd: Pick<ResearchRunEnd, 'state' | 'failureCode' | 'failureMessage'>): { title: string; hint?: string } {
  if (runEnd.state === 'cancelled') return { title: 'Recherche abgebrochen' };
  if (runEnd.state === 'interrupted') return { title: 'Recherche unterbrochen' };
  // D5: der Satz des Servers (etwa „Bahn D ist auf Staging nicht aktiv.“) steht live und nach einem Reload unter der Frage.
  if (runEnd.failureMessage) return { title: 'Recherche fehlgeschlagen', hint: runEnd.failureMessage };
  if (runEnd.failureCode === 'TIMEOUT') {
    return { title: 'Recherche fehlgeschlagen', hint: 'Zeitüberschreitung: Die Recherche hat ihre Höchstdauer überschritten.' };
  }
  return { title: 'Recherche fehlgeschlagen' };
}

/**
 * Der Status unter einer Frage, deren Recherche ohne Antwort endete (abgebrochen, fehlgeschlagen, unterbrochen). Ein
 * Teilergebnis gibt es nicht; „Wiederholen“ startet einen neuen Lauf zu derselben Frage.
 */
export const ResearchRunStatus: React.FC<{
  runEnd: Pick<ResearchRunEnd, 'state' | 'failureCode' | 'failureMessage'>;
  /** Ohne Angabe kein Knopf, etwa wenn die Frage nicht mehr die letzte im Chat ist. */
  onRetry?: () => void;
  disabled?: boolean;
}> = ({ runEnd, onRetry, disabled = false }) => {
  const { title, hint } = researchRunStatusText(runEnd);
  const failed = runEnd.state !== 'cancelled';
  return (
    <div className="flex justify-end" data-testid="research-run-status">
      <div
        role="status"
        className={`flex max-w-3xl items-center gap-3 rounded-lg border px-3 py-2 text-sm ${failed
          ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300'
          : 'border-warm-200 bg-warm-card text-muted dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}
      >
        <span>
          <span className="font-medium">{title}</span>
          {hint && <span className="block text-xs">{hint}</span>}
        </span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={disabled}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-current px-3 py-1.5 text-sm font-medium hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-white/10"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            Wiederholen
          </button>
        )}
      </div>
    </div>
  );
};

/** Der Hinweis über einem laufenden Versuch, wenn der Lauf nach einem Serverneustart von vorn begann (run.attempt_reset). */
export const ResearchRestartNotice: React.FC<{ notice: string }> = ({ notice }) => (
  <p
    role="status"
    data-testid="research-restart-notice"
    className="mb-3 rounded-lg border border-warm-200 bg-warm-card px-3 py-2 text-sm text-muted dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
  >
    {notice}
  </p>
);

export default ResearchRunStatus;
